import type { Lot } from '../types/lot'

import { resolveCanonicalLotKey } from './lotIdentity'

type DashboardSelectableLot = Pick<Lot, 'id' | 'lotKey'>

const normalizeDashboardLotSelectionAlias = (value: string | null | undefined): string => {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return ''

  const canonical = resolveCanonicalLotKey(trimmed)
  return canonical === 'unknown-lot' ? trimmed : canonical
}

const getDashboardLotSelectionAliases = (lot: DashboardSelectableLot): string[] => {
  const aliases = new Set<string>()
  const lotId = String(lot.id ?? '').trim()
  if (lotId) aliases.add(lotId)

  const canonicalLotKey = normalizeDashboardLotSelectionAlias(lot.lotKey)
  if (canonicalLotKey) aliases.add(canonicalLotKey)

  return Array.from(aliases)
}

export const findLotByDashboardSelection = <T extends DashboardSelectableLot>(
  candidate: string,
  lots: ReadonlyArray<T>
): T | null => {
  const trimmedCandidate = String(candidate ?? '').trim()
  if (!trimmedCandidate) return null

  const candidateAliases = new Set<string>([trimmedCandidate])
  const canonicalCandidate = normalizeDashboardLotSelectionAlias(trimmedCandidate)
  if (canonicalCandidate) candidateAliases.add(canonicalCandidate)

  return lots.find((lot) => {
    if (String(lot.lotKey ?? '').trim() === trimmedCandidate) return true
    return getDashboardLotSelectionAliases(lot).some((alias) => candidateAliases.has(alias))
  }) ?? null
}

export const normalizeDashboardLotSelection = (
  candidate: string,
  lots: ReadonlyArray<DashboardSelectableLot>
): string => {
  return findLotByDashboardSelection(candidate, lots)?.id ?? ''
}

export const matchesDashboardLotSelection = (
  selection: string,
  candidate: string | null | undefined,
  lots: ReadonlyArray<DashboardSelectableLot>
): boolean => {
  const trimmedSelection = String(selection ?? '').trim()
  if (!trimmedSelection) return false

  const trimmedCandidate = String(candidate ?? '').trim()
  if (!trimmedCandidate) return false

  const normalizedSelection = normalizeDashboardLotSelection(trimmedSelection, lots)
  const normalizedCandidate = normalizeDashboardLotSelection(trimmedCandidate, lots)
  if (normalizedSelection && normalizedCandidate) {
    return normalizedSelection === normalizedCandidate
  }

  return buildDashboardLotSelectionTokens(trimmedSelection, lots).has(trimmedCandidate)
}

export const buildDashboardLotSelectionTokens = (
  candidate: string,
  lots: ReadonlyArray<DashboardSelectableLot>
): Set<string> => {
  const tokens = new Set<string>()
  const trimmedCandidate = String(candidate ?? '').trim()
  if (!trimmedCandidate) return tokens

  tokens.add(trimmedCandidate)

  const matchedLot = findLotByDashboardSelection(trimmedCandidate, lots)
  if (!matchedLot) return tokens

  getDashboardLotSelectionAliases(matchedLot).forEach((alias) => tokens.add(alias))
  return tokens
}